import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';

interface SecretRevealDialogProps {
  open: boolean;
  secret: string | null;
  /** Distinguishes "we just created this" from "we just rotated it". */
  variant: 'created' | 'rotated';
  onClose: () => void;
}

/**
 * The one and only time a signing secret is visible (REV-442).
 *
 * The server cannot reproduce it: it is stored encrypted and returned exactly
 * once, at creation and at rotation. So this dialog is not a convenience — if
 * the customer dismisses it without copying, their only recourse is to rotate
 * again, which invalidates the secret their receiver may already be using.
 *
 * Three consequences for how it is built:
 *
 * 1. It says so, prominently, BEFORE the secret rather than after. A warning
 *    below the value is read after the value by a sighted user and after the
 *    whole dialog by a screen reader user.
 * 2. Dismissal is a deliberate act — an explicit "I have saved it" button, and
 *    no backdrop-click or Escape close. This is the one place in the app where
 *    an accidental dismissal destroys something, so the usual "let people out
 *    easily" instinct is wrong.
 * 3. The value sits in a readonly text field rather than a <code> block, so it
 *    is focusable, selectable with the keyboard, and announced as a value the
 *    user can act on.
 */
export default function SecretRevealDialog({
  open,
  secret,
  variant,
  onClose,
}: SecretRevealDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      // Clipboard access can be denied. The field is selectable either way, so
      // this is a lost convenience rather than a lost secret — say nothing
      // rather than raise an error the user cannot act on.
      setCopied(false);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      // Neither backdrop click nor Escape closes this. Every other dialog in
      // the app should be escapable; this one destroys the secret on dismissal.
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      aria-labelledby="secret-reveal-title"
      aria-describedby="secret-reveal-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="secret-reveal-title">
        {variant === 'created'
          ? t('webhooks.secret.createdTitle')
          : t('webhooks.secret.rotatedTitle')}
      </DialogTitle>
      <DialogContent>
        {/* Before the value, deliberately: a warning after it is read too late. */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('webhooks.secret.warning')}
        </Alert>
        <DialogContentText id="secret-reveal-description" sx={{ mb: 2 }}>
          {variant === 'created'
            ? t('webhooks.secret.createdDescription')
            : t('webhooks.secret.rotatedDescription')}
        </DialogContentText>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <TextField
            label={t('webhooks.secret.label')}
            value={secret ?? ''}
            fullWidth
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-describedby': 'secret-copy-status' }}
            // Single line, NOT multiline: MUI's textarea wrapper takes a fixed
            // pixel height with overflow:hidden, which stops the value resizing
            // with the user's text size (CSS-06 / WCAG 1.4.4). A secret is one
            // token on one line, so multiline bought nothing and cost that.
            //
            // Monospace so the customer can compare it character by character
            // against what they paste into their receiver.
            sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
          <Tooltip title={t('webhooks.secret.copy')}>
            <IconButton onClick={handleCopy} aria-label={t('webhooks.secret.copy')} sx={{ mt: 1 }}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {/* A live region, so the copy result is announced rather than only seen. */}
        <Box id="secret-copy-status" role="status" aria-live="polite" sx={{ mt: 1, minHeight: 24 }}>
          {copied && (
            <Alert severity="success" sx={{ py: 0 }}>
              {t('webhooks.secret.copied')}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          {t('webhooks.secret.acknowledge')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
